import type { ReactNode } from 'react';
import { SURFACE_COPY } from '@/surfaces/surfaces.copy';

/**
 * The Users surface (SPEC §9).
 *
 * **It had a tab bar and does not any more** (**ADR-0072**). The two tabs were
 * `/users/members` and `/users/recipients`, and the second existed because an
 * agency could hold a list of *other agencies'* people. Nobody holds such a
 * list now (PERM-3, withdrawn), so what is left is one roster — the agency's
 * own members, invited by USR-2, granted by USR-4, revoked by USR-3, unlocked
 * by USR-14 — and §9 says it plainly: a surface with one tab is a surface with
 * no tabs.
 *
 * **The routes went with it, and `/users` is the real one now.** It was a
 * redirect onto `/users/members`, so deleting the members route without moving
 * the content here would have pointed the sidebar's own link at nothing.
 * ADR-0007's rule that navigation is the URL is unchanged and simply has less
 * to do — there is one thing here, and it has one address.
 *
 * This still builds its own heading rather than using `SurfaceHeader`, and that
 * is now the only reason it exists as a component at all: the eyebrow the other
 * three surfaces dropped is dropped here too, and what is left is a title, a
 * summary, and its children. It is kept rather than inlined into `App.tsx`
 * because `SurfaceHeader`'s heading classes are pinned by a test that names
 * this screen (§9's h1 carries the same weight as Storage's and Audit's).
 */

export interface UsersSurfaceProps {
  children: ReactNode;
}

export function UsersSurface({ children }: UsersSurfaceProps) {
  const copy = SURFACE_COPY.users;

  return (
    <section className="bp-animate-fade-in flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h1 className="text-h1 font-bold tracking-tight text-body">{copy.title}</h1>
        {/* No `max-w-prose`: see `SurfaceHeader`'s summary paragraph for why. */}
        <p className="text-sm text-secondary">{copy.summary}</p>
      </div>

      {children}
    </section>
  );
}
